/**
 * Display formatting.
 *
 * Every monetary value arrives from the API as a decimal STRING. These helpers
 * format for display only, and never do arithmetic on the parsed number — the
 * server is the single authority on totals, and the browser must not
 * recalculate them into a slightly different answer.
 */

const currencyFormatters = new Map();

function formatterFor(currency, locale) {
  const key = `${currency}:${locale}`;
  if (!currencyFormatters.has(key)) {
    currencyFormatters.set(
      key,
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  }
  return currencyFormatters.get(key);
}

/** "252725.00" -> "₹2,52,725.00" */
export function money(value, currency = 'INR', locale = undefined) {
  if (value === null || value === undefined || value === '') return '—';
  const asNumber = Number(value);
  if (Number.isNaN(asNumber)) return String(value);

  const resolvedLocale = locale ?? (currency === 'INR' ? 'en-IN' : undefined);
  try {
    return formatterFor(currency, resolvedLocale).format(asNumber);
  } catch {
    // An unknown currency code should not blank out the number.
    return `${currency} ${asNumber.toFixed(2)}`;
  }
}

/** Compact form for dashboard tiles: 1.2L, 3.4Cr, 12.5K. */
export function moneyCompact(value, currency = 'INR') {
  const asNumber = Number(value ?? 0);
  if (Number.isNaN(asNumber)) return '—';

  const symbol = currency === 'INR' ? '\u20B9' : currency === 'USD' ? '$' : `${currency} `;
  const abs = Math.abs(asNumber);

  // Indian numbering when the currency is rupees, since that is what a user
  // reading an INR figure expects.
  if (currency === 'INR') {
    if (abs >= 1e7) return `${symbol}${(asNumber / 1e7).toFixed(2)} Cr`;
    if (abs >= 1e5) return `${symbol}${(asNumber / 1e5).toFixed(2)} L`;
    if (abs >= 1e3) return `${symbol}${(asNumber / 1e3).toFixed(1)}K`;
  } else {
    if (abs >= 1e9) return `${symbol}${(asNumber / 1e9).toFixed(2)}B`;
    if (abs >= 1e6) return `${symbol}${(asNumber / 1e6).toFixed(2)}M`;
    if (abs >= 1e3) return `${symbol}${(asNumber / 1e3).toFixed(1)}K`;
  }

  return money(value, currency);
}

/** "5.000" -> "5"; "2.500" -> "2.5". Trailing zeros are noise in a table. */
export function quantity(value) {
  if (value === null || value === undefined || value === '') return '—';
  const asNumber = Number(value);
  if (Number.isNaN(asNumber)) return String(value);
  return String(Number(asNumber.toFixed(3)));
}

export function number(value) {
  const asNumber = Number(value ?? 0);
  return Number.isNaN(asNumber) ? '—' : asNumber.toLocaleString();
}

export function percent(value) {
  if (value === null || value === undefined || value === '') return '0%';
  return `${Number(value)}%`;
}

// --- dates -----------------------------------------------------------------

export function date(value, { locale = 'en-GB' } = {}) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
}

export function dateTime(value, { locale = 'en-GB' } = {}) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** For an <input type="date"> value. */
export function dateInput(value) {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString().slice(0, 10);
}

/** "3 days ago", "in 2 weeks". Uses Intl so it is correctly pluralised. */
export function relativeTime(value) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';

  const diffMs = parsed.getTime() - Date.now();
  const absMs = Math.abs(diffMs);
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

  const units = [
    ['year', 31_536_000_000],
    ['month', 2_592_000_000],
    ['week', 604_800_000],
    ['day', 86_400_000],
    ['hour', 3_600_000],
    ['minute', 60_000],
  ];

  for (const [unit, ms] of units) {
    if (absMs >= ms) return rtf.format(Math.round(diffMs / ms), unit);
  }
  return 'just now';
}

/** True when a required-by or delivery date has passed. */
export function isOverdue(value) {
  if (!value) return false;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return false;
  return parsed.setHours(23, 59, 59, 999) < Date.now();
}

// --- text ------------------------------------------------------------------

/** "PURCHASE_MANAGER" -> "Purchase manager"; "PARTIALLY_RECEIVED" -> "Partially received" */
export function humanise(value) {
  if (!value) return '';
  const text = String(value).replace(/_/g, ' ').toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** "purchase_order.approved" -> "Purchase order approved" */
export function humaniseAction(action) {
  if (!action) return '';
  return humanise(String(action).replace(/\./g, ' ').replace(/_/g, ' '));
}

export function initials(name, fallback = '?') {
  if (!name) return fallback;
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return fallback;
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts.at(-1)[0]}`.toUpperCase();
}

export function pluralise(count, singular, plural = `${singular}s`) {
  return `${number(count)} ${Number(count) === 1 ? singular : plural}`;
}

export function truncate(value, max = 60) {
  const text = String(value ?? '');
  return text.length > max ? `${text.slice(0, max - 1)}\u2026` : text;
}
